import { useState, useEffect } from 'react';
import { ThemeOptions } from '@mui/material/styles';
import { buildWidgetThemeOptions } from '../chat/createWidgetTheme';
import { FALLBACK_THEME } from '../utils/fallback';
import type { WidgetBrandColors } from '../chat/useWidgetBrandColors';
import {
  CHAT_FONT_SIZE_VALUES,
  WIDGET_LAUNCHER_STYLE_VALUES,
  QUICK_START_PLACEMENT_VALUES,
  QUICK_START_FREQUENCY_VALUES,
  LAUNCHER_TEASER_STYLE_VALUES,
  type ChatFontSize,
  type WidgetLauncherStyle as SharedWidgetLauncherStyle,
  type QuickStartPlacement as SharedQuickStartPlacement,
  type QuickStartFrequency as SharedQuickStartFrequency,
  CHAT_BUTTON_ACCENT_VALUES,
  CHAT_HEIGHT_VALUES,
  type LauncherTeaserStyle as SharedLauncherTeaserStyle,
  type ChatButtonAccent as SharedChatButtonAccent,
  type ChatHeight as SharedChatHeight,
  type SessionTimeout as SharedSessionTimeout,
} from '../../lib/chat-customization';
import { resolveSessionTimeout } from '../../lib/session-timeout';

export type QuickStartButton = {
  label: string;
  message: string;
};

export type ChatAgentFontSize = ChatFontSize;
export type WidgetLauncherStyle = SharedWidgetLauncherStyle;
export type QuickStartPlacement = SharedQuickStartPlacement;
export type QuickStartFrequency = SharedQuickStartFrequency;
export type LauncherTeaserStyle = SharedLauncherTeaserStyle;
export type ChatButtonAccent = SharedChatButtonAccent;
export type ChatHeight = SharedChatHeight;
export type SessionTimeout = SharedSessionTimeout;

export type LauncherTeaserPageOverride = {
  pageUrl: string;
  message: string;
};

function isChatAgentFontSize(value: unknown): value is ChatAgentFontSize {
  return (
    typeof value === 'string' &&
    CHAT_FONT_SIZE_VALUES.includes(value as ChatAgentFontSize)
  );
}

function isWidgetLauncherStyle(value: unknown): value is WidgetLauncherStyle {
  return (
    typeof value === 'string' &&
    WIDGET_LAUNCHER_STYLE_VALUES.includes(value as WidgetLauncherStyle)
  );
}

function isQuickStartPlacement(value: unknown): value is QuickStartPlacement {
  return (
    typeof value === 'string' &&
    QUICK_START_PLACEMENT_VALUES.includes(value as QuickStartPlacement)
  );
}

function isQuickStartFrequency(value: unknown): value is QuickStartFrequency {
  return (
    typeof value === 'string' &&
    QUICK_START_FREQUENCY_VALUES.includes(value as QuickStartFrequency)
  );
}

function isChatButtonAccent(value: unknown): value is ChatButtonAccent {
  return (
    typeof value === 'string' &&
    CHAT_BUTTON_ACCENT_VALUES.includes(value as ChatButtonAccent)
  );
}

function isChatHeight(value: unknown): value is ChatHeight {
  return (
    typeof value === 'string' &&
    CHAT_HEIGHT_VALUES.includes(value as ChatHeight)
  );
}

export type WidgetConfig = {
  theme: Omit<ThemeOptions, 'typography'>;
  widgetBrand: WidgetBrandColors;
  customization: {
    agentName: string;
    /** Institution display name, used in the welcome bubble copy. */
    institutionName?: string | null;
    /** Institution brand logo (kiosk / branding); distinct from agent avatar `logo`. */
    institutionLogoUrl?: string | null;
    chatBoxMarginTop?: number;
    logo?: string;
    privacyPolicyUrl?: string | null;
    privacyNoticeText?: string | null;
    footerText?: string | null;
    showPoweredBy?: boolean;
    /**
     * Institution-level toggle for the chat widget microphone. The widget
     * also gates the mic on browser support; when EITHER is false the mic
     * UI must stay hidden.
     */
    voiceInputEnabled?: boolean;
    whatsappDeflectionEnabled?: boolean;
    whatsappPhoneNumber?: string | null;
    transcriptDownloadEnabled?: boolean;
    quickStartButtons?: QuickStartButton[];
    /** Intro message rendered above the quick start chips. */
    quickStartIntroMessage?: string;
    chatFontSize?: ChatAgentFontSize;
    widgetLauncherStyle?: WidgetLauncherStyle;
    chatButtonAccent?: ChatButtonAccent;
    chatHeight?: ChatHeight;
    sessionTimeout?: SessionTimeout;
    /**
     * Where quick start buttons appear: "within_chat" renders chips inside
     * the chat thread, "on_hover" floats them above the FAB launcher on
     * desktop hover. Mobile viewports always render within_chat (enforced
     * downstream via `useEffectiveQuickStartPlacement`).
     */
    quickStartPlacement?: QuickStartPlacement;
    quickStartFrequency?: QuickStartFrequency;
    launcherTeaserEnabled?: boolean;
    launcherTeaserStyle?: LauncherTeaserStyle;
    launcherTeaserDefaultMessage?: string;
    launcherTeaserPageOverrides?: LauncherTeaserPageOverride[];
    // Fallback support fields - shown when AI service is unavailable
    serviceUnavailableMessage?: string | null;
    supportEmail?: string | null;
    supportPhone?: string | null;
    supportLinkUrl?: string | null;
    supportLinkLabel?: string | null;
    photorealAvatarEnabled?: boolean;
    avatarMaxSessionLengthSeconds?: number;
  };
  features?: {
    captureMclidCookie?: boolean;
    mandatoryIntakeOnOpen?: boolean;
  };
  /** CloudFront viewer country (ISO alpha-2) for this browser session. */
  viewerCountry: string | null;
  analytics?: {
    mixpanelToken: string;
    mixpanelEnabled: boolean;
  };
  serviceDown?: boolean;
};

export type WidgetConfigState = {
  config: WidgetConfig | null;
  loading: boolean;
  error: string | null;
};

type ChatConfigResponse = {
  name: string;
  institutionName: string | null;
  logoUrl?: string | null;
  avatarUrl: string | null;
  chatFontSize?: ChatAgentFontSize;
  widgetLauncherStyle?: WidgetLauncherStyle;
  chatButtonAccent?: ChatButtonAccent;
  chatHeight?: ChatHeight;
  sessionTimeout?: SessionTimeout;
  quickStartPlacement?: QuickStartPlacement;
  quickStartFrequency?: QuickStartFrequency;
  themeColors: {
    primary: { main: string };
    secondary: { main: string };
  };
  privacyPolicyUrl: string | null;
  privacyNoticeText: string | null;
  footerText: string | null;
  showPoweredBy: boolean;
  voiceInputEnabled?: boolean;
  whatsappDeflectionEnabled?: boolean;
  whatsappPhoneNumber?: string | null;
  transcriptDownloadEnabled?: boolean;
  quickStartButtons: QuickStartButton[];
  quickStartIntroMessage?: string;
  launcherTeaserEnabled: boolean;
  launcherTeaserStyle: LauncherTeaserStyle;
  launcherTeaserDefaultMessage: string;
  launcherTeaserPageOverrides: LauncherTeaserPageOverride[];
  serviceUnavailableMessage: string | null;
  supportEmail: string | null;
  supportPhone: string | null;
  analytics?: { mixpanelToken: string; mixpanelEnabled: boolean };
  supportLinkUrl: string | null;
  supportLinkLabel: string | null;
  serviceDown?: boolean;
  photorealAvatarEnabled?: boolean;
  avatarMaxSessionLengthSeconds?: number;
  features?: {
    captureMclidCookie?: boolean;
    mandatoryIntakeOnOpen?: boolean;
  };
  viewerCountry?: string | null;
};

export const useFetchWidgetConfig = (engageId: string): WidgetConfigState => {
  const [config, setConfig] = useState<WidgetConfig | null>(null);
  const [loading, setLoading] = useState<boolean>(() => Boolean(engageId));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchConfig = async () => {
      if (!engageId) {
        const errorMsg = 'No institution was provided.';
        console.error(errorMsg);
        if (!cancelled) {
          setError(errorMsg);
          setLoading(false);
        }
        return;
      }

      try {
        if (!cancelled) {
          setLoading(true);
          setError(null);
        }

        const apiEndpoint = import.meta.env.VITE_CHAT_API_ENDPOINT;
        const s3Url = `${apiEndpoint}/engage/${engageId}/widget-default-config.json`;
        const apiUrl = `${apiEndpoint}/api/chat/v1/institution/${engageId}/chat-config`;

        let data: ChatConfigResponse | null = null;
        let s3NetworkError: unknown = null;

        try {
          const s3Response = await fetch(s3Url, { cache: 'no-store' });
          if (s3Response.ok && !s3Response.redirected) {
            const contentType = s3Response.headers?.get?.('content-type');
            if (!contentType || contentType.includes('application/json')) {
              const parsed = (await s3Response.json().catch(() => null)) as ChatConfigResponse | null;
              if (parsed && typeof parsed === 'object' && 'themeColors' in parsed) {
                data = parsed;
              }
            }
          }
        } catch (err) {
          s3NetworkError = err;
        }

        if (!data) {
          try {
            const response = await fetch(apiUrl, { cache: 'no-store' });
            if (!response.ok) {
              throw new Error(
                `Failed to fetch widget config: ${response.status} ${response.statusText}`
              );
            }
            data = await response.json();
          } catch (apiErr) {
            if (
              s3NetworkError &&
              !(apiErr instanceof Error && apiErr.message.startsWith('Failed to fetch widget config:'))
            ) {
              throw s3NetworkError;
            }
            throw apiErr;
          }
        }

        if (!data) {
          throw new Error('Failed to fetch widget config');
        }

        // Only resolve visitor country from BFF when the intake form is enabled
        // and the config payload didn't already include it (S3 snapshot path).
        const mandatoryIntakeOnOpen =
          data.features?.mandatoryIntakeOnOpen === true;
        let viewerCountry: string | null =
          typeof data.viewerCountry === 'string' ? data.viewerCountry : null;
        if (!viewerCountry && mandatoryIntakeOnOpen) {
          viewerCountry = await fetch(
            `${apiEndpoint}/api/chat/v1/viewer-country`,
            { cache: 'no-store' }
          )
            .then((res) => (res.ok ? res.json() : null))
            .then((json) =>
              typeof json?.viewerCountry === 'string' ? json.viewerCountry : null
            )
            .catch(() => null);
        }

        const primary = data.themeColors.primary.main || FALLBACK_THEME.primary;
        const secondary = data.themeColors.secondary.main || primary;
        const { theme, widgetBrand } = buildWidgetThemeOptions(
          primary,
          secondary
        );

        const newConfig: WidgetConfig = {
          theme,
          widgetBrand,
          customization: {
            agentName: data.name,
            institutionName: data.institutionName,
            institutionLogoUrl: data.logoUrl ?? null,
            logo: data.avatarUrl ?? undefined,
            privacyPolicyUrl: data.privacyPolicyUrl,
            privacyNoticeText: data.privacyNoticeText,
            footerText: data.footerText,
            showPoweredBy: data.showPoweredBy ?? true,
            voiceInputEnabled: data.voiceInputEnabled ?? false,
            whatsappDeflectionEnabled: data.whatsappDeflectionEnabled ?? false,
            whatsappPhoneNumber: data.whatsappPhoneNumber ?? null,
            transcriptDownloadEnabled: data.transcriptDownloadEnabled ?? false,
            quickStartButtons: data.quickStartButtons,
            quickStartIntroMessage: data.quickStartIntroMessage,
            chatFontSize: isChatAgentFontSize(data.chatFontSize)
              ? data.chatFontSize
              : 'sm',
            widgetLauncherStyle: isWidgetLauncherStyle(data.widgetLauncherStyle)
              ? data.widgetLauncherStyle
              : 'icon_with_name',
            chatButtonAccent: isChatButtonAccent(data.chatButtonAccent)
              ? data.chatButtonAccent
              : 'primary_only',
            chatHeight: isChatHeight(data.chatHeight)
              ? data.chatHeight
              : 'full_height',
            sessionTimeout: resolveSessionTimeout(
              typeof data.sessionTimeout === 'string'
                ? data.sessionTimeout
                : undefined
            ),
            quickStartPlacement: isQuickStartPlacement(data.quickStartPlacement)
              ? data.quickStartPlacement
              : 'within_chat',
            quickStartFrequency: isQuickStartFrequency(data.quickStartFrequency)
              ? data.quickStartFrequency
              : 'every_new_chat',
            launcherTeaserEnabled: data.launcherTeaserEnabled ?? false,
            launcherTeaserStyle:
              typeof data.launcherTeaserStyle === 'string' &&
              LAUNCHER_TEASER_STYLE_VALUES.includes(
                data.launcherTeaserStyle as LauncherTeaserStyle
              )
                ? data.launcherTeaserStyle
                : 'message',
            launcherTeaserDefaultMessage:
              data.launcherTeaserDefaultMessage ?? 'Anything we can help with?',
            launcherTeaserPageOverrides: data.launcherTeaserPageOverrides ?? [],
            serviceUnavailableMessage: data.serviceUnavailableMessage,
            supportEmail: data.supportEmail,
            supportPhone: data.supportPhone,
            supportLinkUrl: data.supportLinkUrl,
            supportLinkLabel: data.supportLinkLabel,
            photorealAvatarEnabled: data.photorealAvatarEnabled ?? false,
            avatarMaxSessionLengthSeconds: data.avatarMaxSessionLengthSeconds,
          },
          analytics: data.analytics,
          serviceDown: data.serviceDown,
          features: {
            captureMclidCookie: data.features?.captureMclidCookie === true,
            mandatoryIntakeOnOpen,
          },
          viewerCountry,
        };

        if (!cancelled) {
          setConfig(newConfig);
          setLoading(false);
        }
      } catch (err) {
        const errorMsg = `Error fetching widget config: ${err}`;
        console.error(errorMsg);
        if (!cancelled) {
          setError(errorMsg);
          setLoading(false);
        }
      }
    };

    void fetchConfig();

    return () => {
      cancelled = true;
    };
  }, [engageId]);

  return { config, loading, error };
};
